import React from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const Card = ({ children, sx = {} }) => (
  <Box
    sx={{
      bgcolor: 'background.paper',
      border: '0.5px solid',
      borderColor: 'divider',
      borderRadius: 2,
      p: 2,
      ...sx,
    }}
  >
    {children}
  </Box>
);

const SecLabel = ({ children, sx = {} }) => (
  <Typography
    sx={{
      fontSize: '10px',
      fontWeight: 500,
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      color: 'text.secondary',
      mb: 1.5,
      ...sx,
    }}
  >
    {children}
  </Typography>
);

const fmtDelta = (n) => {
  if (n == null) return { text: '—', color: 'text.secondary' };
  return {
    text:
      (n >= 0 ? '+' : '−') +
      Math.abs(n).toLocaleString(undefined, { maximumFractionDigits: 1 }),
    color: n >= 0 ? '#0F6E56' : '#993C1D',
  };
};

/**
 * Monthly ±2 month headcount table.
 *
 * Props:
 *   title     — section heading
 *   subtitle  — muted caption under the title
 *   months    — ['Jan \'26', 'Feb \'26', …]
 *   planRow   — [85, 90, ...]          planned FTE per month
 *   actualRow — [60, 60, null, ...]    actual FTE (null = future / no completed weeks)
 *   deltaRow  — [-25, -30, null, ...]  actual FTE - plan FTE (null = future)
 *   rows      — optional override: [['Label', values[], bold, isDelta], …]
 *   labelMinWidth — first-column min width (longer GC/CO labels)
 */
const MONTHLY_FTE_TOOLTIP =
  'Avg Weekly FTE = the average weekly crew load (labor hours ÷ 40) across the weeks in that month. It represents the typical crew size that month, not a monthly total. It averages the weeks whose Sunday week ending lands in that month. Actual includes only completed weeks.';

const DEFAULT_SUBTITLE =
  'Work packages only (excludes general conditions & change orders)';

const HCMonthTable = ({
  title,
  subtitle = DEFAULT_SUBTITLE,
  months,
  planRow,
  actualRow,
  deltaRow,
  rows,
  labelMinWidth = 100,
}) => {
  const tableRows =
    rows ||
    [
      ['Planned FTE', planRow, false, false],
      ['Actual FTE', actualRow, true, false],
      ['Delta (FTE)', deltaRow, false, true],
    ];

  return (
    <Card sx={{ flex: 1 }}>
      <Box sx={{ mb: 1.5 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          <SecLabel sx={{ mb: 0 }}>{title}</SecLabel>
          <Tooltip
            title={MONTHLY_FTE_TOOLTIP}
            arrow
            placement="top"
            slotProps={{
              tooltip: {
                sx: { fontSize: '11px', maxWidth: 320, lineHeight: 1.4 },
              },
            }}
          >
            <Box
              component="span"
              tabIndex={0}
              aria-label="Avg Weekly FTE definition"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                cursor: 'help',
                color: 'text.secondary',
              }}
            >
              <InfoOutlinedIcon sx={{ fontSize: 14 }} />
            </Box>
          </Tooltip>
        </Box>
        {subtitle && (
          <Typography
            sx={{
              mt: 0.25,
              fontSize: '9px',
              fontWeight: 400,
              letterSpacing: 0,
              textTransform: 'none',
              color: 'text.secondary',
              lineHeight: 1.3,
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>
      <Table size="small" sx={{ '& .MuiTableCell-root': { px: 1, py: 0.5 } }}>
        <TableHead>
          <TableRow>
            <TableCell
              sx={{
                fontSize: '11px',
                color: 'text.secondary',
                fontWeight: 500,
                minWidth: labelMinWidth,
              }}
            >
              FTE
            </TableCell>
            {months.map((m) => (
              <TableCell
                key={m}
                align="right"
                sx={{
                  fontSize: '11px',
                  color: 'text.secondary',
                  fontWeight: 500,
                }}
              >
                {m}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {tableRows.map(([lbl, vals, bold, isDelta]) => (
            <TableRow key={lbl} sx={bold ? { bgcolor: 'action.hover' } : {}}>
              <TableCell
                sx={{ fontSize: '12px', fontWeight: bold ? 500 : 400 }}
              >
                {lbl}
              </TableCell>
              {vals.map((v, i) => {
                if (v == null) {
                  return (
                    <TableCell
                      key={i}
                      align="right"
                      sx={{ fontSize: '12px', color: 'text.secondary' }}
                    >
                      —
                    </TableCell>
                  );
                }
                if (isDelta) {
                  const { text, color } = fmtDelta(v);
                  return (
                    <TableCell
                      key={i}
                      align="right"
                      sx={{ fontSize: '12px', color }}
                    >
                      {text}
                    </TableCell>
                  );
                }
                return (
                  <TableCell key={i} align="right" sx={{ fontSize: '12px' }}>
                    {typeof v === 'number'
                      ? v.toLocaleString(undefined, {
                          maximumFractionDigits: 1,
                        })
                      : v}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
};

export default HCMonthTable;
